declare let AMapUI: any;

/**
 * 地区加载配置
 */
export interface MapLoadOption {
  amapKey?: string; // 高德可
  id?: string; // 设置地图容器id
  zoom?: number; // 设置当前显示级别
  expandZoomRange?: boolean; // 开启显示范围设置
  zooms?: [number, number]; // 最小显示级别为7，最大显示级别为20
  viewMode?: string; // 特别注意,设置为3D则其他地区不显示
  buildingAnimation?: boolean; // /默认false,表示是否将建筑物动画显示
  rotateEnable?: boolean; // 是否开启地图旋转交互 鼠标右键 + 鼠标画圈移动 或 键盘Ctrl + 鼠标左键画圈移动
  pitchEnable?: boolean; // 是否开启地图倾斜交互 鼠标右键 + 鼠标上下移动或键盘Ctrl + 鼠标左键上下移动
  zoomEnable?: boolean; // 是否可以缩放地图
  resizeEnable?: boolean; // 自适应
  center?: [number, number]; // 设置地图中心点位置
  mapStyle?: string; // 地图样式
  dragEnable?: boolean; // 禁止地图拖动
  terrain?: boolean; // 开启地形图
  layers?: array<any>; // 图层
  mask?: array<any>; // 掩盖区域
  showLabel?: boolean; // 是否展示地图文字和 POI 信息。
  doubleClickZoom?: boolean; // 是否可以双击改变zoom
  features?: array<'bg' | 'point' | 'road' | 'building'>; // 设置地图上显示的元素种类, 支持 'bg' （地图背景）、 'point' （POI点）、 'road' （道路）、 'building' （建筑物）
}

/**
 * map实例
 */
export interface MapConfigureInter {
  on: Fn;
  off: Fn;
  add: Fn;
  emit: Fn;
  remove: Fn;
  destroy?: Fn;
  clearEvents?: Fn;
  addControl?: Fn;
  setCenter?: Fn;
  setZoom?: Fn;
  plugin?: Fn;
  zoomIn?: Fn;
  zoomOut?: Fn;
  getCenter?: Fn;
  getBounds?: Fn;
  setLimitBounds?: Fn;
  getMapType?: Fn;
  setMapType?: Fn;
  resize?: Fn;
  reload?: Fn;
  getPitch?: Fn;
  getRotation?: Fn;
  setRotation?: Fn;
  setPitch?: Fn;
  setFitView?: Fn;
  setMask?: Fn;
  clearMap?: Fn;
  setFeatures?: Fn;
  getFeatures?: Fn;
  panTo?: Fn;
  setZoomAndCenter?: Fn;
  getLocation?: Fn;
  AmbientLight?: string;
  DirectionLight?: string;
  Object3DLayer?: Fn;
  getAllOverlays?: Fn;
  setZooms?: Fn;
  clearLimitBounds?: Fn;
  getZoom?: Fn;
  setMapStyle?: Fn;
  setDefaultCursor?: Fn<any, void>;
  setOptions?: Fn<any, void>;
  clearInfoWindow?: Fn<any, void>;
  lngLatToContainer?: Fn<any, Pixel>;
  containerToLngLat?: Fn<any, Pixel>;
  getContainer?: Fn<
    any,
    {
      offsetWidth?: number;
      offsetHeight?: number;
    }
  >;
  Lights?: {
    AmbientLight?: Fn<any, void>;
    DirectionLight?: Fn<any, void>;
  };
  setStatus: Fn<StatusOption, void>;
  contextMenu: {
    open: Fn<any, void>;
  };
  contextMenuPositon: Object;
}

/**
 * 地图状态
 */
export interface StatusOption {
  dragEnable?: boolean;
  keyboardEnable?: boolean;
  doubleClickZoom?: boolean;
  zoomEnable?: boolean;
  rotateEnable?: boolean;
  scrollWheel?: boolean;
}

/**
 * Amap实例
 */
export interface AMapClass {
  DistrictSearch?: Fn<any, void>;
  Map?: Fn<any, void>;
  Polyline?: Fn<any, void>;
  Bounds?: Fn<any, void>;
  LngLat?: Fn<any, void>;
  TileLayer?: {
    Satellite: () => void; // 静态方法
    RoadNet: () => void;
    new (option: object); // 构造函数返回实例
  };
  Polygon?: Fn<PolygonOption, void>;
  Rectangle?: Fn<any, void>;
  Circle?: Fn<any, void>;
  Object3DLayer: Fn<any, void>;
  DistrictLayer?: {
    Province?: Fn<any, void>;
    Country?: Fn<any, void>;
    World?: Fn<any, void>;
  };
  Object3D?: {
    Wall: Fn<any, void>;
    Prism: Fn<any, void>;
  };
  Map3D: Fn<any, void>;
  HeatMap?: Fn<any, void>;
  Pixel?: Fn<any, void>;
  Marker?: Fn<any, void>;
  MouseTool: MouseTool;
  PolygonEditor?: Fn<any, void>;
  PolylineEditor?: Fn<any, void>;
  CircleEditor?: Fn<any, void>;
  RectangleEditor?: Fn<any, void>;
  ContextMenu?: Fn<any, void>;
  OverlayGroup?: Fn<any, void>;
  Size?: Fn<any, void>;
  VectorLayer?: Fn<any, void>;
  AutoComplete?: Fn<any, void>;
  Geocoder?: Fn<any, void>;
  MarkerClusterer?: Fn<any, void>;
  InfoWindow?: Fn<any, void>;
  Scale?: Fn<any, void>;
  GeoJSON: {
    geoJSON: any;
    getMarker: Fn<any, any>;
    getPolygon: Fn<any, any>;
    getPolyline: Fn<any, any>;
    importData: Fn<any, any>;
    toGeoJSON: Fn<any, any>;
    new (option: object); // 构造函数返回实例
  };
  GeometryUtil?: {
    isPointInPolygon: Fn<any, boolean>;
    isPointInPolygons: Fn<any, boolean>;
    isPointOnLine: Fn<any, boolean>;
    isPointInRing: Fn<any, boolean>;
    isRingInRing: Fn<any, boolean>;
    distanceToSegment: Fn<any, number>;
  };
}

/***
 * 覆盖物集合类型
 */
export interface OverlayGroup {
  addOverlay?: Fn<any, void>;
  addOverlays?: Fn<any, void>;
  importData?: Fn<any, void>;
  removeOverlay?: Fn<any, void>;
  clearOverlays?: Fn<any, void>;
  eachOverlay?: Fn<any, void>;
  hide?: Fn<any, void>;
  show?: Fn<any, void>;
}

/***
 * 信息窗体参数
 */
export interface InfoWindowOption {
  isCustom?: boolean;
  offset: [number, number];
}

/**
 * 多边形配置对象
 */
export interface PolygonOption {
  map?: MapConfigureInter; // 显示该覆盖物的地图对象
  strokeWeight?: number; // 轮廓线宽度
  path?: bounds[i]; // 多边形轮廓线的节点坐标数组
  fillOpacity?: number; // 多边形填充透明度
  fillColor?: string; // 多边形填充颜色
  strokeColor?: string; // 线条颜色
  strokeOpacity?: number; // 线条透明度
  strokeStyle?: string; // 线条样式
  extrusionHeight?: number; // 设置多边形是否拉伸为的立面体高度值，默认值为0
  wallColor?: string; // 立面体侧面颜色
  roofColor?: string; // 立面体顶面颜色
  height?: number; // 设置 polygon 是否离地绘制，默认值为0 。大于0时离地绘制，此时平面高度等于 height 属性值加 polygon 质心点绝对海拔高度值，可以通过getPlaneHeight() 方法获取当前平面高度值（此属性适用于 JSAPI v2.1Beta及以上的版本）。
  bubble?: boolean; // 事件是否冒泡，默认值：false
  extData?: any; // 额为数据
  cursor?: string; // 指定鼠标悬停时的鼠标样式，自定义cursor，IE仅支持cur/ani/ico格式，Opera不支持自定义cursor
  zIndex?: number; // 层级
}

/**
 * 区域查询结果内容
 */
export interface DistrictSearchResult {
  districtList: DistrictSearchInfo[];
  info: string;
}

export interface DistrictSearchInfo {
  boundaries: Array<any>;
  districtList: Array<any>;
  adcode: string;
  level: string;
  cityCode: string;
  name: string;
  center: LngLat;
}

/**
 * 地图编辑器操作栏目搜索表单
 */
export interface AitonBarForm {
  search: string;
  cascaderId: [];
}

/** 标识物对象 */
export interface MarkerOption {
  showMouseMaker?: boolean; // 是否现实鼠标标识物图标
  mouseMakerImg?: string; // 鼠标标识物图片
  mouseRecommendMakerImg?: string; // 鼠标推荐标识物图片
  assetType?: AssetEnum; // 资源类型
  label?: string; // 鼠标标识物图片
  marker?: Marker; // 点标记原对象
  polygon?: Polygon; // 多边形矢量对象
  polyline?: Polyline; // 折线标记对象
  recommendedPoints?: Marker; // 折线标记对象中的标记点
  position?: [number, number]; // 经纬度
  layerId?: number; // 图层id
  mode?: markerMode; // 模式
  lat?: string; // 经度
  lng?: string; // 纬度
}
/** 图形功能对象 */
export interface GraphOption {
  showMouseMaker: boolean; // 是否现实鼠标标识物图标
  target?: any; // TODO: 图形对象
  mode?: GraphMode; // 模式
}

/**
 * 点标记配置项
 * https://lbs.amap.com/api/javascript-api-v2/documentation#marker
 */
export interface AmapMarkerOption {
  position?: [number, number] | LngLat;
  label?: string;
  icon?: string;
  offset?: AMapClass.Pixel;
  visible?: boolean;
  content?: string;
  draggable?: boolean;
  zIndex?: number;
  cursor?: string;
  bubble?: boolean;
  anchor?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'middle-left'
    | 'center'
    | 'middle-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  extData?: any; // 用户自定义属性 ，支持JavaScript API任意数据类型，如 Marker的id等。可将自定义数据保存在该属性上，方便后续操作使用。
}

/**
 * 矢量多边形绘制配置项
 * https://lbs.amap.com/api/javascript-api-v2/documentation#polygon
 */
export interface AmapPolygonOption {
  fillColor?: string; // 多边形填充颜色
  fillOpacity?: number; // 多边形填充透明度
  strokeStyle?: 'solid' | 'dashed'; // strokeStyle是dashed时有效
  strokeDasharray: [number, number];
  strokeColor?: string; // 线条颜色
  strokeOpacity?: number; // 线条透明度
  strokeWeight?: number; // 线条宽度
  bubble?: boolean; // 是否将覆盖物的鼠标或touch等事件冒泡到地图上（自v1.3 新增）
}
/**
 * 矢量圆形绘制配置项
 * https://lbs.amap.com/api/javascript-api-v2/documentation#circle
 */
export interface AmapCircleOption {
  fillColor?: string; // 填充颜色
  fillOpacity?: number; // 填充透明度
  strokeStyle?: 'solid' | 'dashed'; // strokeStyle是dashed时有效
  strokeDasharray: [number, number];
  strokeColor?: string; // 线条颜色
  strokeOpacity?: number; // 线条透明度
  strokeWeight?: number; // 线条宽度
  bubble?: boolean; // 是否将覆盖物的鼠标或touch等事件冒泡到地图上（自v1.3 新增）
}

/**
 * 信息窗体对象
 * @interface
 *
 */
export interface InfoWindow {
  open?: Fn<any, any>;
  close?: Fn<any, any>;
}

/**
 * 点对象
 */
export interface Marker {
  _opts?: any;
  _position?: LngLat;
  _originOpts?: any;
  type?: any;
  map?: any; // 标识物所在地图
  on?: Fn<any, void>;
  off?: Fn<any, void>;
  remove?: Fn<any, void>;
  hide?: Fn<any, void>;
  getExtData?: Fn<any, any>;
  getOptions?: Fn<any, any>;
  setExtData?: Fn<any, any>;
  setSize?: Fn<any, any>;
  setContent?: Fn<any, any>;
  getOffset?: Fn<any, any>;
  getContent?: Fn<any, any>;
  setClickable?: Fn<any, any>;
  setDraggable?: Fn<any, any>;
  getPosition?: Fn<any, LngLat>;
  getVisible?: Fn<any, LngLat>;
  setOptions?: Fn<any, any>;
}

/**
 * 多边形对象
 */
export interface Polygon {
  type?: any;
  on?: Fn<any, void>;
  off?: Fn<any, void>;
  clearEvents?: Fn<any, void>;
  open?: Fn<any, void>;
  getPath?: Fn<any, any>;
  getPosition?: Fn<any, void>;
  getOptions?: Fn<any, any>;
  getExtData?: Fn<any, any>;
  setExtData?: Fn<any, any>;
  setOptions?: Fn<any, any>;
  getArea?: Fn<any, any>;
  getOffset?: Fn<any, any>;
  getBounds?: Fn<any, any>;
  getVisible?: Fn<any, LngLat>;
  destroy?: Fn<any, void>;

  _opts: any;
  map: any;
  _originOpts?: any;
  polygonEditor?: {
    on: Fn<any, void>;
    open: Fn<any, void>;
    close?: Fn<any, void>;
  };
}
/**
 * 矩形对象
 */
export interface Rectangle {
  on?: Fn<any, void>;
  off?: Fn<any, void>;
  clearEvents?: Fn<any, void>;
  open?: Fn<any, void>;
  getBounds?: Fn<any, any>;
  getPosition?: Fn<any, void>;
  getOptions?: Fn<any, any>;
  getExtData?: Fn<any, any>;
  setExtData?: Fn<any, any>;
  setOptions?: Fn<any, any>;
  _opts: any;
  map: any;
  _originOpts?: any;
  polygonEditor?: {
    on: Fn<any, void>;
    open: Fn<any, void>;
    close?: Fn<any, void>;
  };
}
/**
 * 圆形对象
 */
export interface Circle {
  on?: Fn<any, void>;
  off?: Fn<any, void>;
  clearEvents?: Fn<any, void>;
  open?: Fn<any, void>;
  getCenter?: Fn<any, { lng: number; lat: number }>;
  getRadius?: Fn<any, number>;
  getPosition?: Fn<any, void>;
  getOptions?: Fn<any, any>;
  getExtData?: Fn<any, any>;
  setExtData?: Fn<any, any>;
  setOptions?: Fn<any, any>;
  _opts: any;
  map: any;
  _originOpts?: any;
  polygonEditor?: {
    on: Fn<any, void>;
    open: Fn<any, void>;
    close?: Fn<any, void>;
  };
}

/**
 *折线对象
 */

export interface Polyline {
  on?: Fn<any, void>;
  off?: Fn<any, void>;
  getPath?: Fn<any, any>;
  setOptions?: Fn<any, any>;
  getOptions?: Fn<any, any>;
  getExtData?: Fn<any, any>;
  setExtData?: Fn<any, any>;
  destroy?: Fn<any, any>;
  hide?: Fn<any, any>;
  getVisible?: Fn<any, LngLat>;
  setOptions?: Fn<any, any>;
  map: any;
  polyEditor?: {
    on: Fn<any, void>;
    open: Fn<any, void>;
    close?: Fn<any, void>;
    getTarget?: Fn<any, any>;
  };
}

/**
 * 地图右键菜单
 */
export interface ContextMenu {
  addItem?: Fn;
  open?: Fn;
  removeItem?: Fn;
  close?: Fn;
  del?: Fn;
  on?: Fn;
  prototype?: any;
}

/**
 * 地图右键添加按钮
 */
export interface ContextMenuAddItem {
  text: string;
  fn?: Fn;
  num?: number;
}

/**
 * 鼠标工具
 */
export interface MouseTool {
  on?: Fn<any, void>;
  polygon?: Fn<any, any> & {
    on?: Fn<any, any>;
  };
  off?: Fn<any, void>;
  close?: Fn<any, void>;
  polyline?: Fn<any, void>;
  rectangle?: Fn<any, void>;
  circle?: Fn<any, void>;
  new (option: object); // 构造函数返回实例
}

/**
 * 右键菜单配置
 */
export interface ContextMenuOption {
  isCustom?: boolean;
  content?: string;
  deletePolygon?: Fn<any, void>;
}

/**
 *折线配置
 */
export interface PolylineOption {
  path: Array<LngLat> | Array<Array<LngLat>> | Array<Array<Array<LngLat>>>;
  zIndex: number;
  cursor: string;
  isOutline: true;
  outlineColor: string;
  borderWeight: number;
  strokeColor: string;
  strokeOpacity: number;
  strokeWeight: number;
  strokeStyle: 'solid' | 'dashed';
  strokeDasharray: [number, number];
  lineJoin: string;
  lineCap: string;
  zIndex: number;
}

/** 经纬度 */
export interface LngLat {
  lat: string;
  lng: string;
}

/** 热力图实例 */
export interface Heatmap {
  setDataSet?: Fn;
  hide?: Fn;
  show?: Fn;
  getOptions?: Fn;
  setzIndex?: Fn;
}

/** 坐标类型：坐标可以是二维或者三维的数组 */
export type Coordinates = number[] | number[][] | number[][][]; // 根据几何形状，坐标可以是二维、三维数组等

/** 基本的几何类型，GeoJSON 中的几何体 */
export interface Geometry {
  type: GeoTypeEnum;
  coordinates: Coordinates; // 坐标，类型根据几何类型变化
  [key: string]: any;
}

/** 特性（Feature）类型 */
export interface Feature {
  type: 'Feature'; // 特性对象类型
  geometry: Geometry; // 几何体
  properties: { [key: string]: any }; // 属性，可以根据需求添加具体字段
  id?: string | number; // 可选的 ID 字段
}

/** FeatureCollection 类型，包含一个 Feature 数组 */
interface FeatureCollection {
  type: 'FeatureCollection'; // 类型为 FeatureCollection
  properties: { [key: string]: any }; // 属性，可以根据需求添加具体字段
  features: Feature[]; // 存储多个 Feature 对象的数组
}

/** GeoJSON 类型，支持 FeatureCollection */
export type GeoJSONType = FeatureCollection;

/** 设置地图上显示的元素种类，支持bg（地图背景）、point（兴趣点）、road（道路）、building（建筑物） */
export type Features = 'bg' | 'road' | 'building' | 'point';

/** 批量添加覆盖物选项类型 */
export interface AddAllOverlayType {
  getMarker?: Function;
  getPolygon?: Function;
  getPolyline?: Function;
}

/***
 * 覆盖物集合类型
 */
export interface OverlayGroup {
  addOverlay?: Fn<any, void>;
  hasOverlay?: Fn<any, any>;
  getOverlays?: Fn<any, any>;
  addOverlays?: Fn<any, void>;
  removeOverlay?: Fn<any, void>;
  clearOverlays?: Fn<any, void>;
  eachOverlay?: Fn<any, void>;
  hide?: Fn<any, void>;
  show?: Fn<any, void>;
  on?: Fn<any, void>;
}

/**
 * GEO图形类型枚举
 * 定义地理信息系统中的基本图形类型
 */
export enum GeoTypeEnum {
  /** 点类型 */
  marker = 'Point',
  /** 线类型 */
  polyline = 'LineString',
  /** 面类型 */
  polygon = 'Polygon',
}

/** 特性（Feature）类型 */
export interface Feature {
  type: 'Feature'; // 特性对象类型
  geometry: Geometry; // 几何体
  properties: { [key: string]: any }; // 属性，可以根据需求添加具体字段
  id?: string | number; // 可选的 ID 字段
}

/** 定义事件处理器接口 */
export interface AMapEventHandlers {
  [key: string]: any;
}

/**
 * 高德地图事件类型枚举
 */
export enum MapEventEnum {
  /** 点击 */
  click = 'click',
  /** 点击 */
  dblclick = 'dblclick',
  /** 右击 */
  rightclick = 'rightclick',
  /** 移动结束 */
  moveend = 'moveend',
  /** 缩放结束 */
  zoomend = 'zoomend',
  /** 地图缩放级别更改后触发 */
  zoomchange = 'zoomchange',
  /** 地图加载完成 */
  complete = 'complete',
  /** 打开 */
  open = 'open',
  /** 关闭 */
  close = 'close',
  /** 结束 */
  end = 'end',
  /** 添加 */
  add = 'add',
  /** 鼠标移入 */
  mouseover = 'mouseover',
  /** 鼠标移出 */
  mouseout = 'mouseout',
  /** 拖动结束 */
  dragend = 'dragend',
  /** 显示 */
  show = 'show',
  /** 隐藏 */
  hide = 'hide',
  /** 绘制 */
  draw = 'draw',
  /** 添加节点 */
  addnode = 'addnode',
  /** 鼠标移动 */
  mousemove = 'mousemove',
  /** 调整节点 */
  adjust = 'adjust',
  /** 地图视角更改后触发 */
  pitchchange = 'pitchchange',
}

/**
 * 图形当前参数
 */
export interface GraphOptions {
  type?: GraphMode;
  canEdit?: boolean;
  zIndex?: number;
  [key: string]: any;
}
